import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { shopsData } from '../../mocks/shops';

const HomePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = ['All', 'Food', 'Café', 'Craft', 'Bar', 'Community'];

  const featuredShops = shopsData.filter(shop => shop.featured);

  const filteredShops = selectedCategory === 'All' 
    ? shopsData 
    : shopsData.filter(shop => shop.category === selectedCategory);

  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "まーやいびーが？ Ryukyu ゆいまーる MAP",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
      "description": "沖縄・琉球人オーナーのお店をマップで見つけて、あなたの「好き」と「応援」をつなぐためのサイトです。",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/shops?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F0] via-[#FFFAF7] to-[#FFF8F3]">
      <Header />

      {/* Hero Section with Map */}
      <section className="pt-[96px] px-6 md:px-10 pb-16" style={{ minHeight: '80vh' }}>
        <div className="relative w-full h-full" style={{ minHeight: 'calc(80vh - 96px)' }}>
          {/* Map Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="ryukyu-map-container relative w-full h-full rounded-[32px] overflow-hidden"
            style={{
              boxShadow: '0 24px 80px rgba(255, 107, 53, 0.18), 0 8px 32px rgba(255, 140, 66, 0.12)',
              minHeight: '550px'
            }}
          >
            {/* Map Iframe */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d913339.4355317331!2d128.0272586!3d26.5929685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sja!2sjp!4v1767139877292!5m2!1sja!2sjp"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '550px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="琉球弧マップ"
            ></iframe>

            {/* Text Overlay Box */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="absolute bottom-10 left-10 max-w-[520px] p-10 rounded-[32px]"
              style={{
                backdropFilter: 'blur(60px) saturate(180%)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 250, 247, 0.92) 100%)',
                border: '1.5px solid rgba(255, 107, 53, 0.25)',
                boxShadow: '0 20px 60px rgba(255, 107, 53, 0.25), 0 4px 20px rgba(255, 140, 66, 0.15)'
              }}
            >
              <h1 className="text-[32px] font-bold leading-[1.5] text-[#2D1810] mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                <span className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent">まーやいびーが？</span>
                <br />
                <span className="text-[26px]">琉球にルーツを持つオーナーのお店を<br />地図からめぐろう。</span>
              </h1>
              <p className="text-[15px] leading-[1.9] text-[rgba(45,24,16,0.7)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                琉球人オーナーのお店をマップで見つけて、あなたの「好き」と「応援」をつなぐためのサイトです。
              </p>
            </motion.div>

            {/* Category Filter Pills */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-8 right-8 flex flex-wrap gap-2.5"
            >
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-[14px] font-semibold transition-all duration-400 hover:-translate-y-1 hover:scale-105 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white shadow-[0_8px_24px_rgba(255,107,53,0.45)]'
                      : 'bg-white/90 backdrop-blur-sm border-[1.5px] border-[#FFD4C0] text-[#FF6B35] hover:bg-white hover:shadow-[0_6px_20px_rgba(255,107,53,0.3)]'
                  }`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Shops Section */}
      <section className="py-24 px-6">
        <div className="max-w-[1240px] mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16 text-center"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#FFE8DC] to-[#FFF0E6] mb-5">
              <span className="text-[13px] font-bold bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent tracking-[1.5px] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                ✦ PICK UP
              </span>
            </div>
            <h2 className="text-[42px] font-bold bg-gradient-to-r from-[#2D1810] to-[#FF6B35] bg-clip-text text-transparent leading-[1.3] mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              注目のお店
            </h2>
            <p className="text-[16px] text-[rgba(45,24,16,0.65)] max-w-[600px] mx-auto" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              琉球の心を大切にする、こだわりのお店をご紹介
            </p>
          </motion.div>

          {/* Shop Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredShops.map((shop, index) => (
              <motion.div
                key={shop.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/shop/${shop.id}`}
                  className="group block bg-white rounded-[28px] overflow-hidden shadow-[0_8px_32px_rgba(255,107,53,0.15)] hover:shadow-[0_24px_64px_rgba(255,107,53,0.3)] hover:-translate-y-3 transition-all duration-500 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative w-full h-72 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(255,107,53,0.2)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                    <img
                      src={shop.image}
                      alt={shop.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-7">
                    <h3 className="text-[22px] font-bold text-[#2D1810] mb-3 group-hover:text-[#FF6B35] transition-colors duration-300" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {shop.name}
                    </h3>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C42]"></div>
                        <span className="text-[14px] text-[rgba(45,24,16,0.65)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                          {shop.area}
                        </span>
                      </div>
                      <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FFE8DC] to-[#FFF0E6] text-[13px] font-semibold bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent">
                        {shop.category}
                      </span>
                    </div>
                    <p className="text-[15px] leading-[1.8] text-[rgba(45,24,16,0.65)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {shop.catchphrase}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-center mt-16"
          >
            <Link
              to="/shops"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-[16px] font-semibold shadow-[0_12px_32px_rgba(255,107,53,0.35)] hover:shadow-[0_16px_48px_rgba(255,107,53,0.45)] hover:-translate-y-1 hover:scale-105 transition-all duration-400 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              すべてのお店を見る
              <i className="ri-arrow-right-line text-[18px]"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
